import api from '../lib/api';

export interface Reserva {
  id: string;
  user_id: string;
  sala_id: string;
  bloco_id: string;
  data: string;
  valor: number;
  status: 'RESERVADO' | 'CONFIRMADO' | 'CONCLUIDO' | 'CANCELADO' | 'EXPIRADO' | 'USADO' | 'EM_USO';
  pago_com_credito: boolean;
  valor_credito_usado?: number;
  expira_em: string;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    nome_completo: string;
    email: string;
    telefone: string;
  };
  sala: {
    id: string;
    nome: string;
    descricao?: string;
  };
  bloco: {
    id: string;
    hora_inicio: string;
    hora_fim: string;
    valor: number;
  };
}

export interface CreateReservaData {
  user_id: string;
  sala_id: string;
  bloco_id: string;
  data: string;
  pago_com_credito?: boolean;
  valor_credito_usado?: number;
}

export interface UpdateReservaData {
  status?: string;
  pago_com_credito?: boolean;
  valor_credito_usado?: number;
}

export interface ReservaFilters {
  user_id?: string;
  sala_id?: string;
  status?: string;
  data_inicio?: string;
  data_fim?: string;
  page?: number;
  limit?: number;
}

export interface ReservaListResponse {
  data: Reserva[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ReservaEstatisticas {
  totalReservas: number;
  reservasHoje: number;
  reservasAtivas: number;
  reservasCanceladas: number;
}

export interface CalendarioAdmin {
  data: Date;
  salas: Array<{
    id: string;
    nome: string;
    blocos: Array<{
      id: string;
      hora_inicio: string;
      hora_fim: string;
      valor: number;
      reserva?: {
        id: string;
        cliente: string;
        status: string;
        user_id: string;
      };
    }>;
  }>;
}

class ReservaService {
  async create(data: CreateReservaData): Promise<Reserva> {
    const response = await api.post('/api/reservas', data);
    return response.data.data;
  }

  async findById(id: string): Promise<Reserva> {
    const response = await api.get(`/api/reservas/${id}`);
    return response.data.data;
  }

  async findAll(filters: ReservaFilters = {}): Promise<ReservaListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/api/reservas?${params.toString()}`);
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async update(id: string, data: UpdateReservaData): Promise<Reserva> {
    const response = await api.put(`/api/reservas/${id}`, data);
    return response.data.data;
  }

  async cancel(id: string, motivo: string): Promise<Reserva> {
    const response = await api.patch(`/api/reservas/${id}/cancelar`, { motivo });
    return response.data.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/api/reservas/${id}`);
  }

  async getReservasByUser(userId: string, filters: Omit<ReservaFilters, 'user_id'> = {}): Promise<ReservaListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/api/reservas/usuario/${userId}?${params.toString()}`);
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async getReservasBySala(salaId: string, filters: Omit<ReservaFilters, 'sala_id'> = {}): Promise<ReservaListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/api/reservas/sala/${salaId}?${params.toString()}`);
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async getReservasHoje(): Promise<ReservaListResponse> {
    const response = await api.get('/api/reservas/hoje');
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async getEstatisticas(): Promise<ReservaEstatisticas> {
    const response = await api.get('/api/reservas/estatisticas');
    return response.data.data;
  }

  async getCalendarioAdmin(data: string): Promise<CalendarioAdmin> {
    const response = await api.get(`/api/reservas/calendario-admin?data=${data}`);
    return response.data.data;
  }

  // Métodos auxiliares para formatação
  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'RESERVADO': 'Reservado',
      'CONFIRMADO': 'Confirmado',
      'EM_USO': 'Em Uso',
      'USADO': 'Usado',
      'CONCLUIDO': 'Concluído',
      'CANCELADO': 'Cancelado',
      'EXPIRADO': 'Expirado'
    };
    return statusMap[status] || status;
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'RESERVADO': 'bg-yellow-100 text-yellow-800',
      'CONFIRMADO': 'bg-blue-100 text-blue-800',
      'CONCLUIDO': 'bg-green-100 text-green-800',
      'CANCELADO': 'bg-red-100 text-red-800',
      'EXPIRADO': 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('pt-BR');
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}

export default new ReservaService(); 